// ----------------------------------------------------------------------
// <copyright file="Izenda.Dashboard.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Dashboard
 */

!(function() {
  var m = require('mobx');
  var trackable = require('share/tracking/Izenda.Trackable');
  var CategoryDetail = require('../Izenda.CategoryDetail');
  var DashboardDetail = require('./Izenda.DashboardDetail');
  var DashboardConstants = require('logic/data/Izenda.DashboardConstants');
  var DashboardDesignerMode = require('./Izenda.DashboardDesignerMode');
  var uiState = require('logic/Izenda.UiState');
  const { isNil, remove, isEmpty, isEqual, find } = require('lodash/fp');
  var DashboardList = require('logic/models/Izenda.BoardList')(DashboardConstants, 'dashboards');

  function Dashboard(data) {
    this.isLeftMenuLastPage = false;

    var props = {
      leftMenuCategories: [],
      leftMenuCategoryTree: [],
      categoryTree: [], // It's a array of the CategoryDetail item
      dashboardDetail: new DashboardDetail(), // For new/edit dashboard
      items: new DashboardList(),
      dashboardDesignerMode: new DashboardDesignerMode(), // Control button status
      autoDurationMinute: 0,
      autoDurationSecond: 0,
      localGlobalCategories: []
    };

    m.extendObservable(trackable(this), props);
    data && this.populateData(data);
  }

  /**
   * Clone dashboard object
   */
  Dashboard.prototype.clone = m.action('clone dashboard', function(saveAs) {
    var dashboard = new Dashboard();
    dashboard.dashboardDetail = new DashboardDetail(this.dashboardDetail);

    /**
     * if dashboard is global, it is being opened in a tenant or not a system admin
     * when save as, reset dashboard to local, reset its cat/subcategory.
     */
    if (
      dashboard.dashboardDetail.dashboard.isGlobal &&
      (!uiState.currentUser.systemAdmin || !isNil(uiState.tenantId))
    ) {
      dashboard.dashboardDetail.dashboard.isGlobal = false;
      dashboard.dashboardDetail.dashboard.categoryId = null;
      dashboard.dashboardDetail.dashboard.categoryName = null;
      dashboard.dashboardDetail.dashboard.subCategoryId = null;
      dashboard.dashboardDetail.dashboard.subCategoryName = null;
    }
    dashboard.dashboardDetail.saveAs = saveAs;
    return dashboard;
  });

  /**
   * Populate data
   * @param  {Object} dashboard object
   */
  Dashboard.prototype.populateData = m.action('load dashboard', function(data) {
    m.transaction(
      function() {
        this.categoryTree = data.categoryTree;
        this.dashboardDetail = data.dashboardDetail;
        this.dashboardList = data.dashboardList;
        this.dashboardDesignerMode = data.dashboardDesignerMode;
        this.autoDurationMinute = data.autoDurationMinute;
        this.autoDurationSecond = data.autoDurationSecond;
      }.bind(this)
    );
  });

  /**
   * create new Dashboard
   * @method createNewDashboard
   */
  Dashboard.prototype.createNewDashboard = m.action('new dashboard', function() {
    this.dashboardDetail = new DashboardDetail();
  });

  /**
   * Get category tree for dashboard
   * @param {object} category list
   */
  Dashboard.prototype.readDashboardCategoryTree = m.action('load category tree', function(categoryList) {
    categoryList = categoryList || [];

    this.categoryTree = categoryList.map(function(item) {
      if (!isEmpty(item.subCategories)) {
        item.subCategories = item.subCategories.map(function(subItem) {
          return new CategoryDetail(subItem);
        });
      }
      return new CategoryDetail(item);
    });

    var allDashboardItem = new CategoryDetail();
    allDashboardItem.populateData(DashboardConstants.ALL_CATEGORY_TYPES.ALL_DASHBOARDS);
    this.categoryTree.splice(0, 0, allDashboardItem);
  });

  /**
   * Delete a ReportCategory out of reportCategoryTree
   * @param {object} reportCategory
   * @returns {promise}
   */
  Dashboard.prototype.removeCategory = m.action('remove category', function(category) {
    if (!category.isSubCategory) {
      this.categoryTree = remove({ id: category.id }, this.categoryTree);
    } else {
      var parent = find({ id: category.parentId }, this.categoryTree);
      parent.subCategories = remove({ id: category.id }, parent.subCategories);
    }
  });

  /**
   *  Add new category in save popup
   */
  Dashboard.prototype.addCategory = m.action('add category', function(category) {
    this.dashboardDetail.updateSubCategory();

    this.markDirty();
  });

  /**
   *  Add new sub-category in save popup
   */
  Dashboard.prototype.addSubCategory = m.action('add subCategory', function(categoryId, subCategory) {
    this.markDirty();
  });

  /**
   * Get category
   * @param {String} categoryId
   */
  Dashboard.prototype.getAllCategory = m.action(function(categories) {
    this.categoryTree = categories;
  });

  /**
   * Get sub category
   * @param {String} categoryId
   */
  Dashboard.prototype.getSubCategory = function(categoryId) {
    var category = find(function(c) {
      return isEqual(c.id, categoryId);
    }, this.categoryTree);
    return category && category.subCategories;
  };

  /**
   * Update auto duration minute
   * @param {Numeric} auto duration
   */
  Dashboard.prototype.updateAutoDurationMinute = function(minute) {
    this.autoDurationMinute = minute;
  };

  /**
   * Update auto duration second
   * @param {Numeric} auto duration
   */
  Dashboard.prototype.updateAutoDurationSecond = function(second) {
    this.autoDurationSecond = second;
  };

  /**
   * create all item
   * @returns {Object} all item
   */
  Dashboard.prototype.createAllItem = function() {
    var item = new CategoryDetail();
    var data = DashboardConstants.ALL_CATEGORY_TYPES.ALL_DASHBOARDS;
    item.populateData(data);
    return item;
  };

  /**
   * Clean list accessor for global dashboard
   */
  Dashboard.prototype.cleanListAccessors = m.action('cleanListAccessors', function() {
    var dashboardDetail = this.dashboardDetail;
    var isGlobal = dashboardDetail.dashboard.isGlobal;
    var accessModel = dashboardDetail.accessModel;
    // Clear all sharing with user if change to global
    isGlobal && accessModel.clearAllUserAccessor();
    // Clean all global roles on access detail.
    !isGlobal && accessModel.removeAllGlobalRolesOnAccessor();
  });

  module.exports = Dashboard;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Dashboard/Izenda.Dashboard.js